import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { ContasPagarGetAllRequest } from '../../requests/financas/contas-pagar-get-all.request';
import { ContasPagarGetAllResponse } from '../../responses/financas/contas-pagar-get-all.response';
import { ContasPagarBulkRequest } from '../../requests/financas/contas-pagar-bulk.request';
import { ContasPagarBulkAlterarFormaPagamentoRequest } from '../../requests/financas/contas-pagar-bulk-alterar-forma-pagamento.request';
import { ContasPagarBulkCategoriaRequest, ContasPagarBulkEditRequest, ContasPagarBulkTagsRequest } from '../../requests/financas/contas-pagar.request';
import { GetLookupsContasPagarResponse } from '../../responses/financas/get-lookups-contas-pagar.response';
import { ReturnStruct } from '../../structs/return.struct';

@Injectable({
    providedIn: 'root',
})
export class ContasPagarService extends CommonService {
    private readonly apiUrl: string = environment.urlApiFinancas + 'ContasPagar';

    constructor(private httpClient: HttpClient) {
        super();
    }

    public getLookups(): Observable<GetLookupsContasPagarResponse> {
        const url = `${this.apiUrl}/lookups`;
        return this.httpClient
            .get<GetLookupsContasPagarResponse>(url, this.addHeaderToken())
            .pipe(catchError(this.handleError.bind(this)));
    }

    public getAll(request: ContasPagarGetAllRequest, pagina: number = 0, tamanhoPagina: number = 20): Observable<ContasPagarGetAllResponse> {
        const params = new HttpParams()
            .set('pagina', pagina.toString())
            .set('tamanhoPagina', tamanhoPagina.toString());

        const url = `${this.apiUrl}/getAll`;

        return this.httpClient
            .post<ContasPagarGetAllResponse>(url, request, { params, ...this.addHeaderToken() })
            .pipe(catchError(this.handleError));
    }

    public getPrint(request: ContasPagarGetAllRequest): Observable<ContasPagarGetAllResponse> {
        const url = `${this.apiUrl}/print`;
        return this.httpClient
            .post<ContasPagarGetAllResponse>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public getById(id: number): Observable<any> {
        const url = `${this.apiUrl}/idContasPagar/${id}`;
        return this.httpClient
            .get<any>(url, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public create(request: any): Observable<ReturnStruct> {
        return this.httpClient
            .post<ReturnStruct>(this.apiUrl, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public update(request: any): Observable<ReturnStruct> {
        return this.httpClient
            .put<ReturnStruct>(this.apiUrl, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public bulkDelete(request: ContasPagarBulkRequest): Observable<ReturnStruct> {
        const url = `${this.apiUrl}/bulk-delete`;
        return this.httpClient
            .delete<ReturnStruct>(url, { body: request, ...this.addHeaderToken() })
            .pipe(catchError(this.handleError));
    }

    public bulkCancelar(request: ContasPagarBulkRequest): Observable<ReturnStruct> {
        const url = `${this.apiUrl}/bulk-cancelar`;
        return this.httpClient
            .put<ReturnStruct>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public simularParcelas(request: any): Observable<any> {
        const url = `${this.apiUrl}/simular-parcelas`;
        return this.httpClient
            .post<any>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public simularRecorrencia(request: any): Observable<any> {
        const url = `${this.apiUrl}/simular-recorrencia`;
        return this.httpClient
            .post<any>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public renegociarParcelas(request: any): Observable<ReturnStruct> {
        const url = `${this.apiUrl}/renegociar`;
        return this.httpClient
            .post<ReturnStruct>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public getValorRenegociacao(idContasPagar: number): Observable<any> {
        const url = `${this.apiUrl}/valor-renegociacao/${idContasPagar}`;
        return this.httpClient
            .get<any>(url, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public getArquivoContasPagar(urlArquivo: string): Observable<any> {
        const params = new HttpParams().set('urlArquivo', urlArquivo);
        const url = `${this.apiUrl}/getArquivoContasPagar`;
        return this.httpClient
            .get<any>(url, { params, ...this.addHeaderToken() })
            .pipe(catchError(this.handleError));
    }

    public validarAgrupamento(request: { idsParcelas: number[] }): Observable<any> {
        const url = `${this.apiUrl}/validar-agrupamento`;
        return this.httpClient
            .post<any>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public agrupar(request: any): Observable<ReturnStruct> {
        const url = `${this.apiUrl}/agrupar`;
        return this.httpClient
            .post<ReturnStruct>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public desagrupar(idContasPagarConsolidado: number): Observable<ReturnStruct> {
        const url = `${this.apiUrl}/desagrupar/${idContasPagarConsolidado}`;
        return this.httpClient
            .post<ReturnStruct>(url, {}, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public editarEmMassa(request: ContasPagarBulkEditRequest): Observable<ReturnStruct> {
        const url = `${this.apiUrl}/bulk`;
        return this.httpClient
            .patch<ReturnStruct>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public alterarCategoriaEmMassa(request: ContasPagarBulkCategoriaRequest): Observable<ReturnStruct> {
        const url = `${this.apiUrl}/bulk-categoria`;
        return this.httpClient
            .patch<ReturnStruct>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public alterarTagsEmMassa(request: ContasPagarBulkTagsRequest): Observable<ReturnStruct> {
        const url = `${this.apiUrl}/bulk-tags`;
        return this.httpClient
            .patch<ReturnStruct>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public estornarPagamento(idContasPagarParcela: number): Observable<ReturnStruct> {
        const url = `${this.apiUrl}/estorno`;
        return this.httpClient
            .post<ReturnStruct>(url, { idContasPagarParcela }, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    public bulkAlterarFormaPagamento(request: ContasPagarBulkAlterarFormaPagamentoRequest): Observable<ReturnStruct> {
        const url = `${this.apiUrl}/bulk-forma-pagamento`;
        return this.httpClient
            .patch<ReturnStruct>(url, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }
}
